import {
  Link,
  Toast,
  ToastTitle,
  ToastTrigger,
  useToastController,
  type ToastIntent,
} from '@fluentui/react-components';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_TOASTER_ID } from '@/shared/components/app-toaster';

export interface Notice {
  intent: ToastIntent;
  /** Plain-language message, already translated (never a raw error text). */
  message: string;
}

/**
 * Announces the outcome of a mutation in the app's toaster (docs/plan/12-frontend.md §11). A
 * success or an information fades after a readable delay; a problem stays until dismissed.
 */
export function useNotify(): (notice: Notice) => void {
  const { t } = useTranslation('common');
  const { dispatchToast } = useToastController(APP_TOASTER_ID);
  return useCallback(
    ({ intent, message }: Notice) => {
      const fades = intent === 'success' || intent === 'info';
      dispatchToast(
        <Toast>
          <ToastTitle
            action={
              <ToastTrigger>
                <Link>{t('actions.close')}</Link>
              </ToastTrigger>
            }
          >
            {message}
          </ToastTitle>
        </Toast>,
        { intent, timeout: fades ? 8000 : -1, politeness: fades ? 'polite' : 'assertive' },
      );
    },
    [dispatchToast, t],
  );
}
